import styled from '@emotion/styled';
import { Chip, Typography, useMediaQuery } from '@material-ui/core';
import EventIcon from '@material-ui/icons/Event';
import { createElement, FC, Fragment, useEffect, useReducer } from 'react';
import { FormattedMessage } from 'react-intl';

import ProductPricing from '../../../entity/ProductPricing';
import { AvailableDeliveryDueDate } from '../../../generated/graphql';
import theme from '../../../theme';
import { calendarize } from '../../../utility/calendarize';
import { DateWithoutTime, getYMD, isSameYMD, nextMonth, predMonth, toYMD } from '../../../utility/dateUtil';
import mq from '../../styleUtility/mediaquery';

type PassedProps = {
  /** 特急日の表出制御に使われる */
  isPointRequired: boolean;
  /** 特急日の表出制御に使われる */
  productPricing?: ProductPricing;
  availableDeliveryDueDates: ReadonlyArray<AvailableDeliveryDueDate>;
  /** 渡された onChangeDeliveryDueDays. usecase の実行 */
  selectDate: (days: number | null, expressDueDays: number) => void;
  /** n営業日以内。初期表示時に必要なのでpropsで受け取る */
  deliveryDueDays: number | null;
};

type ContainerProps = {
  handleClickMonthSelector: (cursor: 'next' | 'prev') => void;
  selectedDate: DateWithoutTime;
  selectedMonth: DateWithoutTime;
  confirmedDate: DateWithoutTime;
  calendar: DateWithoutTime[][];
  handleClick: (selectedDate: number) => void;
  handleConfirm: () => void;
  due: AvailableDeliveryDueDate;
  isModalOpen: boolean;
  setOpen: (state: boolean) => void;
  cancel: () => void;
};

type Props = PassedProps &
  ContainerProps & {
    className?: string;
  };

const Component: FC<Props> = ({
  selectedDate,
  handleClickMonthSelector,
  calendar,
  availableDeliveryDueDates,
  className,
  handleClick,
  handleConfirm,
  isPointRequired,
  productPricing,
  due,
  isModalOpen,
  setOpen,
  cancel,
  selectedMonth,
  confirmedDate,
}) => {
  const isMd = useMediaQuery(`(min-width:${theme.breakpoints.values['md']}px)`);
  return (
    <div className={className}>
      <div className="row">
        <Typography>
          <FormattedMessage id="delivery_due_date.label" />：
        </Typography>
        <div
          className="dateInput"
          onClick={() => {
            setOpen(!isModalOpen);
          }}
        >
          {confirmedDate.year}/{confirmedDate.month}/{confirmedDate.date}
          {!isMd ? <CalendarIcon /> : null}
        </div>
        <FlexWrap>
          {due.expressDueDays && isPointRequired && productPricing ? (
            // 特急のバッジ
            <FlexColumn>
              <FormattedMessage
                id={'creative_format_editor.creation_set_list.express_delivery'}
                values={{
                  days: due.expressDueDays,
                }}
              >
                {(text: string) => (
                  <span>
                    <Chip label={text} />
                  </span>
                )}
              </FormattedMessage>
            </FlexColumn>
          ) : null}
          <FormattedMessage id={'available_delivery_due_date.business_days'} values={{ days: due.days }} />
        </FlexWrap>
      </div>
      {isModalOpen && (
        <div className="modalContainer">
          <div className="modalInner">
            <div className="calendarHeader">
              <span
                onClick={() => {
                  handleClickMonthSelector('prev');
                }}
                className="arrow"
              >
                {'<'}
              </span>
              <h3 className="title">
                <Typography>
                  {selectedMonth.year}/{selectedMonth.month}
                </Typography>
              </h3>
              <span
                onClick={() => {
                  handleClickMonthSelector('next');
                }}
                className="arrow"
              >
                {'>'}
              </span>
            </div>

            <table className="calendar">
              <tbody>
                <tr>
                  {[['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => <td key={`${d}-${i}`}>{d}</td>)]}
                </tr>
                {calendar.map((week, idx) => (
                  <tr key={`${week[0]}-${idx}`}>
                    {week.map((day, idx) => (
                      <td
                        key={`${day.date}-${idx}`}
                        onClick={
                          availableDeliveryDueDates
                            .map((date) => getYMD(new Date(date.dueDate)))
                            .filter((dueDate) => isSameYMD(dueDate, day)).length > 0
                            ? () => handleClick(day.date)
                            : undefined
                        }
                        className={
                          availableDeliveryDueDates
                            .map((date) => getYMD(new Date(date.dueDate)))
                            .filter((dueDate) => isSameYMD(dueDate, day)).length > 0
                            ? selectedDate.date === day.date && selectedDate.month === selectedMonth.month
                              ? 'selectable selected'
                              : 'selectable'
                            : undefined
                        }
                      >
                        {day.date > 0 ? day.date : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="buttonRow">
              <span onClick={cancel}>CANCEL</span>
              <span onClick={handleConfirm}>OK</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StyledComponent = styled(Component)`
  & > .row {
    display: flex;
    align-items: center;
    min-height: 32px;

    ${mq('sp')} {
      flex-direction: column;
      align-items: initial;
      justify-content: center;
    }

    > * + * {
      margin-left: 12px;

      ${mq('sp')} {
        margin-left: initial;
        margin-top: 12px;
      }
    }
  }

  & .dateInput {
    border-bottom: solid 1px rgba(0, 0, 0, 0.87);
    padding: 8px;
    padding-right: 24px;
    cursor: pointer;

    ${mq('sp')} {
      position: relative;
      border: 1px solid #cbd5e0;
    }
  }

  & > .modalContainer {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1200;

    & > .modalInner {
      padding: 20px;
      background-color: white;
      border-radius: 8px;

      & > .calendarHeader {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 18px;

        & > .arrow {
          color: rgba(0, 0, 0, 0.38);
          cursor: pointer;
          display: block;
          padding: 4px;
          border-radius: 4px;

          &:hover {
            background: #eeeeee;
          }
        }
      }

      & > .calendar {
        & td {
          color: gray;
          width: 36px;
          height: 36px;
          border-radius: 18px;
          text-align: center;
          cursor: not-allowed;
        }

        & td.selectable {
          cursor: pointer;
          color: initial;

          :hover {
            background: #eeeeee;
          }
        }

        & td.selected {
          background-color: #00a46e;
          color: white;

          :hover {
            background-color: #00a46e;
          }
        }
      }

      & > .buttonRow {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        color: #00a46e;
        cursor: pointer;

        > * {
          display: block;
          padding: 12px;
          border-radius: 4px;

          &:hover {
            background: #eeeeee;
          }
        }

        > * + * {
          margin-left: 12px;
        }
      }
    }
  }
`;

const CalendarIcon = styled(EventIcon)`
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  right: 5px;
`;

const FlexWrap = styled('div')`
  ${mq('sp')} {
    display: flex;
    align-items: center;
  }
`;

const FlexColumn = styled('div')`
  display: inline;
  margin-right: 10px;
`;

const ContainerComponent: FC<PassedProps> = (passedProps) => {
  const { availableDeliveryDueDates, deliveryDueDays, selectDate } = passedProps;
  const [state, dispatch] = useReducer(reducer, initialState);
  const { calendarCursor, isModalOpen, selectedDate, due, confirmedDate } = state;
  const calendar = calendarize(calendarCursor.year, calendarCursor.month).map((week) =>
    week.map((day) => ({ ...calendarCursor, date: day })),
  );

  useEffect(() => {
    if (availableDeliveryDueDates.length === 0) {
      return;
    }

    /** 特急n日 */
    let expressDueDays = 0;
    if (deliveryDueDays) {
      const date = availableDeliveryDueDates.find((deliveryDueDate) => deliveryDueDate.days === deliveryDueDays);
      if (date) {
        dispatch(initWithSavedData({ availableDeliveryDueDates: [...availableDeliveryDueDates], deliveryDueDays }));
      }
      expressDueDays = date!.expressDueDays;
    }

    selectDate(deliveryDueDays, expressDueDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickMonthSelector = (selector: 'next' | 'prev') => {
    if (selector === 'next') {
      dispatch(goNextMonth());
    } else if (selector === 'prev') {
      dispatch(goPrevMonth());
    }
  };

  /**
   * カレンダーから日付を選択した時に発火
   * @param date 日付
   */
  const handleClick = (date: number) => {
    dispatch(setDate(date));
  };

  /**
   * カレンダーから日付を確定させたときに発火
   */
  const handleConfirm = () => {
    dispatch(confirmDate());
    dispatch(toggleModal());
  };

  // due が切り替わると callback を呼び出してAPIへ保存
  useEffect(() => {
    if (due === undefined) return;
    selectDate(due.days, due.expressDueDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [due]);

  /**
   * モーダルのキャンセルボタンをクリックした時に発火
   */
  const cancel = () => {
    dispatch(toggleModal());
  };

  if (due) {
    const containerProps: ContainerProps = {
      handleClickMonthSelector,
      selectedDate,
      confirmedDate,
      selectedMonth: calendarCursor,
      calendar,
      handleClick,
      handleConfirm,
      due,
      isModalOpen,
      setOpen: () => dispatch(toggleModal()),
      cancel,
    };
    return <StyledComponent {...{ ...passedProps }} {...{ ...containerProps }} />;
  } else {
    return <Fragment />;
  }
};

const ACTIONS = {
  USER_SELECT_DATE: 'USER_SELECT_DATE',
  USER_CONFIRM_DATE: 'USER_CONFIRM_DATE',
  GO_NEXT_MONTH: 'GO_NEXT_MONTH',
  GO_PREV_MONTH: 'GO_PREV_MONTH',
  TOGGLE_MODAL: 'TOGGLE_MODAL',
  INIT_WITH_SAVED_DATA: 'INIT_WITH_SAVED_DATA',
} as const;

const initWithSavedData = (data: {
  availableDeliveryDueDates: AvailableDeliveryDueDate[];
  deliveryDueDays: number;
}) => {
  return { type: ACTIONS.INIT_WITH_SAVED_DATA, payload: data };
};

const setDate = (date: number) => {
  return { type: ACTIONS.USER_SELECT_DATE, payload: date };
};

const confirmDate = () => {
  return { type: ACTIONS.USER_CONFIRM_DATE };
};

const goNextMonth = () => {
  return { type: ACTIONS.GO_NEXT_MONTH };
};

const goPrevMonth = () => {
  return { type: ACTIONS.GO_PREV_MONTH };
};

const toggleModal = () => {
  return { type: ACTIONS.TOGGLE_MODAL };
};

type ActionType =
  | ReturnType<typeof initWithSavedData>
  | ReturnType<typeof setDate>
  | ReturnType<typeof goNextMonth>
  | ReturnType<typeof goPrevMonth>
  | ReturnType<typeof toggleModal>
  | ReturnType<typeof confirmDate>;

type State = {
  selectedDate: DateWithoutTime;
  calendarCursor: DateWithoutTime;
  confirmedDate: DateWithoutTime;
  due: AvailableDeliveryDueDate | undefined;
  isModalOpen: boolean;
  availableDeliveryDueDates: AvailableDeliveryDueDate[];
};

const initialState: State = {
  selectedDate: getYMD(new Date()),
  confirmedDate: getYMD(new Date()),
  calendarCursor: getYMD(new Date()),
  due: undefined,
  isModalOpen: false,
  availableDeliveryDueDates: [],
};

const reducer = (state: State, action: ActionType): State => {
  switch (action.type) {
    case 'INIT_WITH_SAVED_DATA': {
      const { availableDeliveryDueDates, deliveryDueDays } = action.payload;
      const due = availableDeliveryDueDates.find((deliveryDueDate) => deliveryDueDate.days === deliveryDueDays);
      const newestDate = getYMD(new Date(due ? due.dueDate : new Date().toISOString()));
      return {
        ...state,
        availableDeliveryDueDates: availableDeliveryDueDates,
        due: due ?? availableDeliveryDueDates[0],
        calendarCursor: newestDate,
        selectedDate: newestDate,
        confirmedDate: newestDate,
      };
    }
    case 'USER_SELECT_DATE': {
      const ymd: DateWithoutTime = toYMD({
        year: state.calendarCursor.year,
        month: state.calendarCursor.month,
        date: action.payload,
      });
      return {
        ...state,
        selectedDate: ymd,
        calendarCursor: ymd,
      };
    }
    case 'USER_CONFIRM_DATE': {
      const due = state.availableDeliveryDueDates
        // availableDeliveryDueDates を 独自日付型に変換
        .map((dueDate) => {
          return { ...dueDate, ymd: getYMD(new Date(dueDate.dueDate)) };
        })
        // availableDeliveryDueDates の中からカレンダーで選択された日付と一致する日付を抽出
        .filter(
          (date) =>
            date.ymd.date === state.selectedDate.date &&
            date.ymd.month === state.selectedDate.month &&
            date.ymd.year === state.selectedDate.year,
        )[0];
      return {
        ...state,
        due: { days: due.days, dueDate: due.dueDate, expressDueDays: due.expressDueDays },
        confirmedDate: state.selectedDate,
      };
    }
    case 'GO_NEXT_MONTH': {
      const next = nextMonth(state.calendarCursor);
      return { ...state, calendarCursor: next };
    }
    case 'GO_PREV_MONTH': {
      const prev = predMonth(state.calendarCursor);
      return { ...state, calendarCursor: prev };
    }
    case 'TOGGLE_MODAL': {
      return { ...state, isModalOpen: !state.isModalOpen };
    }
    default:
      throw new Error('unexpected operation');
  }
};

export const AvailableDeliveryDueDateCalendar = ContainerComponent;
